/**
 * Get all supported timezones from the Intl API
 */
export function getTimeZones(): string[] {
  return (Intl as any).supportedValuesOf?.('timeZone') ?? [];
}

/**
 * Calculate the timezone offset in minutes for a given timezone at a specific date
 * @param tz - The timezone string (e.g., 'America/New_York')
 * @param at - The date to calculate the offset for (defaults to current date)
 * @returns The offset in minutes
 */
export function tzOffsetMinutes(tz: string, at = new Date()): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }).formatToParts(at);

  const get = (t: string): number => {
    const part = parts.find(p => p.type === t);
    return part ? Number(part.value) : 0;
  };

  const local = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour'),
    get('minute'),
    get('second')
  );
  const utc = new Date(at).setMilliseconds(0);
  return (local - utc) / 60000;
}

/**
 * Format a timezone offset in minutes as a string like "(UTC +05:30)" or "(UTC -08:00)"
 * @param mins - The offset in minutes
 * @returns The formatted offset string
 */
export function formatOffset(mins: number): string {
  const sign = mins >= 0 ? '+' : '-';
  const abs = Math.abs(mins);
  const hh = String(Math.floor(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  return `(UTC ${sign}${hh}:${mm})`;
}

/**
 * Get all timezone options formatted for use in a select dropdown,
 * sorted by UTC offset and then alphabetically by timezone name
 * @returns Array of timezone options with value and label properties
 */
export function getTimeZoneOptions(): Array<{ value: string; label: string }> {
  const zones = getTimeZones();

  return zones
    .map(z => {
      const m = tzOffsetMinutes(z);
      return {
        value: z,
        label: `${formatOffset(m)} ${z.replace(/_/g, ' ')}`
      };
    })
    .sort(
      (a, b) =>
        tzOffsetMinutes(a.value) - tzOffsetMinutes(b.value) ||
        a.value.localeCompare(b.value)
    );
}
